import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Headphones, Menu, X } from "lucide-react";
import gamrentLogo from "../../assets/gamrent-logo.jpg";

const Navbar = ({ onLoginClick, onSignupClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    const mq = window.matchMedia("(min-width: 1024px)");
    const onResize = () => mq.matches && setMobileOpen(false);
    document.addEventListener("keydown", onKeyDown);
    mq.addEventListener("change", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      mq.removeEventListener("change", onResize);
    };
  }, [mobileOpen]);

  const isActive = p => location.pathname === p;

  const links = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/features" },
    { name: "How It Works", path: "/how-it-works" },
    { name: "Pricing", path: "/pricing" },
    { name: "Contact", path: "/contact" },
  ];

  const handleLogin = () => {
    if (onLoginClick) {
      onLoginClick();
      return;
    }
    navigate("/login");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-teal-900 text-teal-50 text-xs pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 flex items-center justify-between gap-3">
          <p className="truncate">Property and Rental Management Platform</p>
          <button onClick={() => navigate("/contact")} className="shrink-0 inline-flex items-center gap-1.5 font-semibold hover:text-teal-200 transition-colors">
            <Headphones className="w-3.5 h-3.5" aria-hidden="true" />
            Need support?
          </button>
        </div>
      </div>

      <nav className={`transition-all duration-300 border-b ${scrolled ? "bg-teal-50/95 backdrop-blur border-teal-200 shadow-sm" : "bg-teal-50 border-teal-100"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-[74px] flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 flex-shrink-0 group">
            <div className="w-10 h-10 rounded-md overflow-hidden flex items-center justify-center shadow-sm border border-teal-200 transition-transform duration-200 group-hover:scale-105">
              <img src={gamrentLogo} alt="Gamrent Logo" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
              <p className="text-lg leading-tight font-extrabold text-teal-900">Gamrent</p>
            </div>
          </button>

          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {links.map((l) => (
              <button
                key={l.path}
                onClick={() => navigate(l.path)}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive(l.path)
                    ? "text-teal-700 bg-teal-50"
                    : "text-teal-800 hover:text-teal-700 hover:bg-teal-100"
                }`}
              >
                {l.name}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleLogin}
              className="px-5 py-2.5 border border-teal-700 text-teal-700 text-sm font-bold rounded-md transition-colors hover:bg-teal-700 hover:text-white"
            >
              Sign In
            </button>
            <button
              onClick={() => onSignupClick ? onSignupClick() : navigate("/signup")}
              className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-md transition-colors"
            >
              Start Free
            </button>
          </div>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2.5 -mr-1 rounded-xl text-teal-800 hover:bg-teal-100 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-teal-50 border-t border-teal-200 shadow-md max-h-[calc(100dvh-100px-env(safe-area-inset-top))] overflow-y-auto overscroll-contain">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.path}
                  onClick={() => navigate(l.path)}
                  className={`text-left px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
                    isActive(l.path) ? "bg-teal-100 text-teal-700" : "text-teal-800 hover:bg-teal-100"
                  }`}
                >
                  {l.name}
                </button>
              ))}
              <div className="border-t border-teal-200 mt-2 pt-3 grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogin();
                  }}
                  className="w-full px-4 py-3 border border-teal-700 text-teal-700 font-bold rounded-md text-sm transition-colors hover:bg-teal-700 hover:text-white"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    onSignupClick ? onSignupClick() : navigate("/signup");
                  }}
                  className="w-full px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-md text-sm transition-colors"
                >
                  Start Free
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
      </header>
  );
};

export default Navbar;
